import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Plus, X } from 'lucide-react';
import {
  useResourceGroups,
  useResources,
  type PractitionerEligibilityMode,
  type ServiceResourceRequirementInput,
} from '@/hooks/useSchedulingResources';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { filterPractitionerMembers, withStickySelectedMembers } from '@/lib/practitionerRole';
import { useFormsTemplateList, type FormsTemplateSummary } from '@/hooks/useFormsTemplates';
import {
  automationsFromLegacyFields,
  legacyFieldsFromAutomations,
  type QuestionnaireAutomationDraft,
} from '@/lib/preVisitQuestionnaireTiming';
import { QuestionnairePhaseList } from './QuestionnaireAutomationFields';
import { parsePaymentTiming, type PaymentTiming } from '@/lib/bookingPolicies';
import { Button } from '@/components/ui/Button';
import { FieldError, FieldHint } from '@/components/ui/Field';
import { FIELD_CONTROL } from '@/components/ui/fieldClasses';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { VatMomsField } from '@/components/ui/VatMomsField';
import { useClinic } from '@/hooks/useClinicSettings';
import { clinicVatRatePercent, itemIncludesVat, itemVatRatePercent } from '@/lib/vat';
import { cn } from '@/utils/cn';
import {
  FORM_HINT_TYPE,
  FORM_LABEL_TYPE,
  MODAL_TITLE_TYPE,
  TYPE,
  UI_SECONDARY,
} from '@/lib/typography';
import type { ServiceInput, ServiceRow } from '@/hooks/useServices';
import {
  SERVICE_DURATION_HOURS_ID,
  SERVICE_DURATION_MAX_HOURS,
  SERVICE_DURATION_MINUTE_PART_MAX,
  SERVICE_DURATION_MINUTES_ID,
  combineServiceDuration,
  splitServiceDuration,
} from '@/lib/serviceDuration';
import {
  buildServiceEditorSummary,
  formatResourceRequirementSummary,
  formatServicePrice,
  hasConfiguredQuestionnaires,
  type ServiceEditorPaymentTiming,
  type ServiceEditorSummary,
} from './serviceEditorSummary';

const RADIO_INPUT = 'h-4 w-4 border-ds-field text-ds-teal focus:shadow-ring';

function paymentTimingCopy(timing: ServiceEditorPaymentTiming, t: (key: string) => string): string {
  switch (timing) {
    case 'clinic_default':
      return t('booking_policies.service_use_clinic_default');
    case 'upon_booking':
      return t('booking_policies.upon_booking');
    case 'at_appointment':
      return t('booking_policies.at_appointment');
    default: {
      const _exhaustive: never = timing;
      return _exhaustive;
    }
  }
}

function practitionerSummaryCopy(
  summary: ServiceEditorSummary,
  t: (key: string) => string,
): string {
  switch (summary.practitionerMode) {
    case 'all':
      return t('services.practitioners_all');
    case 'selected':
      return summary.practitionerNames.length > 0
        ? summary.practitionerNames.join(', ')
        : t('services.practitioners_selected');
    default: {
      const _exhaustive: never = summary.practitionerMode;
      return _exhaustive;
    }
  }
}

export function ServiceFormModal({
  clinicId,
  form,
  onChange,
  editing,
  isBlocker,
  requirements,
  onRequirementsChange,
  practitionerEligibility,
  onPractitionerEligibilityChange,
  selectedPractitionerIds,
  onSelectedPractitionerIdsChange,
  onClose,
  onSubmit,
  pending,
}: {
  clinicId: string | null;
  form: ServiceInput;
  onChange: (next: ServiceInput) => void;
  editing: ServiceRow | null;
  isBlocker: boolean;
  requirements: ServiceResourceRequirementInput[];
  onRequirementsChange: (next: ServiceResourceRequirementInput[]) => void;
  practitionerEligibility: PractitionerEligibilityMode;
  onPractitionerEligibilityChange: (next: PractitionerEligibilityMode) => void;
  selectedPractitionerIds: string[];
  onSelectedPractitionerIdsChange: (next: string[]) => void;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  pending: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { data: clinic } = useClinic(clinicId);
  const { data: resourceGroups } = useResourceGroups(clinicId);
  const { data: resources } = useResources(clinicId);
  const { data: members } = useClinicMembers(clinicId);
  const { data: roles, isSuccess: rolesReady } = useClinicRoles(clinicId);
  const checklistMembers = useMemo(() => {
    const all = members ?? [];
    if (!rolesReady) return all;
    return withStickySelectedMembers(
      filterPractitionerMembers(all, roles ?? []),
      all,
      selectedPractitionerIds,
    );
  }, [members, roles, rolesReady, selectedPractitionerIds]);
  const clinicVatRate = clinicVatRatePercent(clinic);
  const { data: templateLibrary, isLoading: templatesLoading } = useFormsTemplateList(
    clinicId ?? undefined,
  );

  const allTemplates = [...(templateLibrary?.platform ?? []), ...(templateLibrary?.clinic ?? [])];
  const locale = i18n.language.startsWith('en') ? 'en-GB' : 'da-DK';

  const practitionerNamesById = useMemo(
    () => Object.fromEntries((members ?? []).map((member) => [member.user_id, member.name])),
    [members],
  );

  const summary = buildServiceEditorSummary({
    form,
    practitionerEligibility,
    selectedPractitionerIds,
    practitionerNamesById,
    requirements,
    resourceGroups: resourceGroups ?? [],
    resources: resources ?? [],
  });

  const questionnairesConfigured = hasConfiguredQuestionnaires(summary);
  const [pricingOpen, setPricingOpen] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(true);
  const [questionnairesOpen, setQuestionnairesOpen] = useState(questionnairesConfigured);

  const title = editing
    ? isBlocker
      ? t('services.edit_blocker_title', 'Rediger blokering')
      : t('services.edit_title')
    : isBlocker
      ? t('services.new_blocker', 'Ny blokering')
      : t('services.new_service');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-editor-title"
        data-testid="service-editor"
        className="mx-auto my-6 flex max-h-[calc(100vh-3rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-ds-card shadow-pop"
      >
        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-ds-line px-6 py-4">
            <h3 id="service-editor-title" className={MODAL_TITLE_TYPE}>
              {title}
            </h3>
            <ServiceEditorSummaryHeader summary={summary} locale={locale} />
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4">
            <section
              data-testid="service-section-basic"
              className="space-y-4 rounded-lg border border-ds-line px-4 py-4"
            >
              <h4 className={cn(TYPE.section, 'text-ds-text')}>{t('services.section_basic')}</h4>
              <BasicInformationFields
                form={form}
                onChange={onChange}
                durationGroupLabel={
                  isBlocker
                    ? t('services.field_duration_group_blocker')
                    : t('services.field_duration_group')
                }
              />
            </section>

            {!isBlocker && (
              <ServiceEditorSection
                id="pricing"
                title={t('services.section_pricing')}
                summary={`${formatServicePrice(summary.priceCents, summary.currency, locale)} · ${paymentTimingCopy(summary.paymentTiming, t)}`}
                open={pricingOpen}
                onToggle={() => setPricingOpen((value) => !value)}
              >
                <PricingFields
                  form={form}
                  onChange={onChange}
                  clinicVatRate={clinicVatRate}
                  clinicPaymentTiming={parsePaymentTiming(clinic?.payment_timing)}
                />
              </ServiceEditorSection>
            )}

            {!isBlocker && (
              <ServiceEditorSection
                id="booking"
                title={t('services.booking_requirements')}
                description={t('services.booking_requirements_hint')}
                summary={practitionerSummaryCopy(summary, t)}
                open={bookingOpen}
                onToggle={() => setBookingOpen((value) => !value)}
              >
                <BookingRequirementsFields
                  requirements={requirements}
                  onRequirementsChange={onRequirementsChange}
                  practitionerEligibility={practitionerEligibility}
                  onPractitionerEligibilityChange={onPractitionerEligibilityChange}
                  selectedPractitionerIds={selectedPractitionerIds}
                  onSelectedPractitionerIdsChange={onSelectedPractitionerIdsChange}
                  members={checklistMembers}
                  resourceGroups={resourceGroups ?? []}
                  resources={resources ?? []}
                />
              </ServiceEditorSection>
            )}

            {!isBlocker && (
              <ServiceEditorSection
                id="questionnaires"
                title={t('services.section_questionnaires')}
                description={t('services.section_questionnaires_hint')}
                summary={
                  questionnairesConfigured
                    ? t('services.summary_questionnaires_counts', {
                        before: summary.questionnairesBefore,
                        after: summary.questionnairesAfter,
                      })
                    : t('services.summary_questionnaires_none')
                }
                open={questionnairesOpen}
                onToggle={() => setQuestionnairesOpen((value) => !value)}
              >
                <QuestionnairesFields
                  form={form}
                  onChange={onChange}
                  templates={allTemplates}
                  templatesLoading={templatesLoading}
                />
              </ServiceEditorSection>
            )}

            <section
              data-testid="service-section-status"
              className="rounded-lg border border-ds-line px-4 py-4"
            >
              <h4 className={cn(TYPE.section, 'text-ds-text')}>{t('services.section_status')}</h4>
              <p className={cn('mt-1', FORM_HINT_TYPE)}>{t('services.section_status_hint')}</p>
              <label className="mt-3 inline-flex items-center gap-2 text-sm text-ds-text">
                <input
                  type="checkbox"
                  checked={form.is_active ?? true}
                  onChange={(e) => onChange({ ...form, is_active: e.target.checked })}
                  className={RADIO_INPUT}
                />
                {t('services.field_is_active')}
              </label>
            </section>
          </div>

          <div className="flex justify-end gap-2 border-t border-ds-line px-6 py-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ServiceEditorSummaryHeader({
  summary,
  locale,
}: {
  summary: ServiceEditorSummary;
  locale: string;
}) {
  const { t } = useTranslation();
  const statusLabel = summary.isActive
    ? t('services.status_active')
    : t('services.status_inactive');
  const metaParts = summary.isBlocker
    ? [`${summary.durationMinutes} ${t('services.minutes_short')}`, statusLabel]
    : [
        `${summary.durationMinutes} ${t('services.minutes_short')}`,
        formatServicePrice(summary.priceCents, summary.currency, locale),
        statusLabel,
      ];

  return (
    <div className="mt-3" data-testid="service-editor-summary">
      <p className={cn(TYPE.card, 'text-ds-text')}>
        {summary.name || t('services.summary_untitled')}
      </p>
      <p className={cn('mt-0.5', UI_SECONDARY)}>{metaParts.join(' · ')}</p>
      {!summary.isBlocker && (
        <dl className={cn('mt-2 space-y-0.5', UI_SECONDARY)}>
          <div>
            <dt className="inline font-medium text-ds-muted">
              {t('services.summary_practitioners')}:
            </dt>{' '}
            <dd className="inline">{practitionerSummaryCopy(summary, t)}</dd>
          </div>
          <div>
            <dt className="inline font-medium text-ds-muted">{t('services.summary_resources')}:</dt>{' '}
            <dd className="inline">
              {summary.resources.length > 0
                ? summary.resources.map(formatResourceRequirementSummary).join(' · ')
                : t('services.resources_none')}
            </dd>
          </div>
          <div>
            <dt className="inline font-medium text-ds-muted">
              {t('services.summary_questionnaires')}:
            </dt>{' '}
            <dd className="inline">
              {hasConfiguredQuestionnaires(summary)
                ? t('services.summary_questionnaires_counts', {
                    before: summary.questionnairesBefore,
                    after: summary.questionnairesAfter,
                  })
                : t('services.summary_questionnaires_none')}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function ServiceEditorSection({
  id,
  title,
  description,
  summary,
  open,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  summary?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const headingId = `service-section-${id}-heading`;
  const panelId = `service-section-${id}-panel`;

  return (
    <section data-testid={`service-section-${id}`} className="rounded-lg border border-ds-line">
      <h4>
        <button
          type="button"
          id={headingId}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={
            open
              ? t('services.collapse_section', { section: title })
              : t('services.expand_section', { section: title })
          }
          onClick={onToggle}
          className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left focus:outline-none focus-visible:shadow-ring"
        >
          <span className="min-w-0">
            <span className={cn(TYPE.section, 'block text-ds-text')}>{title}</span>
            {open && description ? (
              <span className={cn('mt-0.5 block', FORM_HINT_TYPE)}>{description}</span>
            ) : null}
            {!open && summary ? (
              <span className={cn('mt-0.5 block', UI_SECONDARY)}>{summary}</span>
            ) : null}
          </span>
          <ChevronDown
            className={cn(
              'mt-0.5 h-4 w-4 shrink-0 text-ds-muted transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </h4>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={headingId}
          className="space-y-4 border-t border-ds-line px-4 py-4"
        >
          {children}
        </div>
      )}
    </section>
  );
}

function ServiceDurationField({
  minutes,
  onMinutesChange,
  groupLabel,
}: {
  minutes: number;
  onMinutesChange: (minutes: number) => void;
  groupLabel: string;
}) {
  const { t } = useTranslation();
  const syncedMinutes = useRef(minutes);
  const initial = splitServiceDuration(minutes);
  const [hoursDraft, setHoursDraft] = useState(() => String(initial.hours));
  const [minutesDraft, setMinutesDraft] = useState(() => String(initial.minutes));
  const total = combineServiceDuration(hoursDraft, minutesDraft);
  const invalid = total == null;
  const describedBy = invalid ? 'service-duration-error' : 'service-duration-hint';

  useEffect(() => {
    if (minutes === syncedMinutes.current) return;
    syncedMinutes.current = minutes;
    const parts = splitServiceDuration(minutes);
    setHoursDraft(String(parts.hours));
    setMinutesDraft(String(parts.minutes));
  }, [minutes]);

  function commit(next: number) {
    syncedMinutes.current = next;
    onMinutesChange(next);
  }

  function update(nextHours: string, nextMinutes: string) {
    setHoursDraft(nextHours);
    setMinutesDraft(nextMinutes);
    const parsed = combineServiceDuration(nextHours, nextMinutes);
    if (parsed != null) commit(parsed);
  }

  function normalize() {
    const parsed = combineServiceDuration(hoursDraft, minutesDraft);
    if (parsed == null) return;
    const parts = splitServiceDuration(parsed);
    setHoursDraft(String(parts.hours));
    setMinutesDraft(String(parts.minutes));
  }

  return (
    <div className="relative pt-6">
      <p
        id="service-duration-group-label"
        className={cn('absolute left-0 top-0 whitespace-nowrap', FORM_LABEL_TYPE)}
      >
        {groupLabel}
      </p>
      <div
        className="flex items-start gap-4"
        role="group"
        aria-labelledby="service-duration-group-label"
      >
        <div className="w-20 shrink-0" data-testid="service-duration-hours-field">
          <Input
            id={SERVICE_DURATION_HOURS_ID}
            name={SERVICE_DURATION_HOURS_ID}
            label={t('services.field_duration_hours')}
            type="number"
            inputMode="numeric"
            min={0}
            max={SERVICE_DURATION_MAX_HOURS}
            step={1}
            required
            value={hoursDraft}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            onChange={(event) => update(event.target.value, minutesDraft)}
            onBlur={normalize}
          />
        </div>
        <div className="w-20 shrink-0" data-testid="service-duration-minutes-field">
          <Input
            id={SERVICE_DURATION_MINUTES_ID}
            name={SERVICE_DURATION_MINUTES_ID}
            label={t('services.field_duration_minutes')}
            type="number"
            inputMode="numeric"
            min={0}
            max={SERVICE_DURATION_MINUTE_PART_MAX}
            step={1}
            required
            value={minutesDraft}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            onChange={(event) => update(hoursDraft, event.target.value)}
            onBlur={normalize}
          />
        </div>
      </div>
      {invalid ? (
        <FieldError id="service-duration-error">{t('services.field_duration_invalid')}</FieldError>
      ) : (
        <FieldHint id="service-duration-hint">{t('services.field_duration_hint')}</FieldHint>
      )}
    </div>
  );
}

export function BasicInformationFields({
  form,
  onChange,
  durationGroupLabel,
}: {
  form: ServiceInput;
  onChange: (next: ServiceInput) => void;
  /** Shown above Timer/Minutter without shifting that row. Blockers can pass their own label. */
  durationGroupLabel?: string;
}) {
  const { t } = useTranslation();
  const groupLabel = durationGroupLabel ?? t('services.field_duration_group');

  return (
    <>
      <div
        className="flex flex-col gap-4 sm:flex-row sm:items-start"
        data-testid="service-basic-fields"
      >
        <div className="min-w-0 flex-1 sm:pt-6">
          <Input
            name="service-name"
            label={t('services.field_name')}
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
            required
          />
        </div>
        <ServiceDurationField
          minutes={form.duration_minutes}
          onMinutesChange={(duration_minutes) => onChange({ ...form, duration_minutes })}
          groupLabel={groupLabel}
        />
      </div>

      <Textarea
        id="service-description"
        name="service-description"
        label={t('services.field_description')}
        value={form.description ?? ''}
        onChange={(e) => onChange({ ...form, description: e.target.value })}
        rows={2}
        placeholder={t('services.field_description_placeholder')}
      />

      <div className="max-w-[12rem]">
        <label htmlFor="service-color" className={cn('block', FORM_LABEL_TYPE)}>
          {t('services.field_color')}
        </label>
        <input
          id="service-color"
          type="color"
          value={form.color ?? '#6366F1'}
          onChange={(e) => onChange({ ...form, color: e.target.value })}
          className="mt-1 h-[42px] w-full cursor-pointer rounded-md border border-ds-field bg-ds-card p-1"
        />
      </div>
    </>
  );
}

export function PricingFields({
  form,
  onChange,
  clinicVatRate,
  clinicPaymentTiming,
}: {
  form: ServiceInput;
  onChange: (next: ServiceInput) => void;
  clinicVatRate: number;
  clinicPaymentTiming: PaymentTiming;
}) {
  const { t } = useTranslation();
  const overrideEnabled = form.payment_timing != null;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          name="service-price"
          label={t('services.field_price', { currency: form.currency ?? 'DKK' })}
          type="number"
          min={0}
          step={1}
          value={(form.price_cents / 100).toString()}
          onChange={(e) =>
            onChange({
              ...form,
              price_cents: Math.round(Number(e.target.value) * 100),
            })
          }
        />
        <VatMomsField
          includesVat={itemIncludesVat(form.vat_rate_percent)}
          clinicRatePercent={clinicVatRate}
          onChange={(includesVat) =>
            onChange({
              ...form,
              vat_rate_percent: itemVatRatePercent(includesVat, clinicVatRate),
            })
          }
        />
      </div>

      <fieldset>
        <legend className={FORM_LABEL_TYPE}>{t('services.field_payment_timing')}</legend>
        <div className="mt-2 space-y-2">
          <label className="flex items-center gap-2 text-sm text-ds-text">
            <input
              type="radio"
              name="service-payment-timing-mode"
              checked={!overrideEnabled}
              onChange={() => onChange({ ...form, payment_timing: null })}
              className={RADIO_INPUT}
            />
            {t('booking_policies.service_use_clinic_default')}
          </label>
          <label className="flex items-center gap-2 text-sm text-ds-text">
            <input
              type="radio"
              name="service-payment-timing-mode"
              checked={overrideEnabled}
              onChange={() =>
                onChange({
                  ...form,
                  payment_timing: form.payment_timing ?? clinicPaymentTiming,
                })
              }
              className={RADIO_INPUT}
            />
            {t('services.payment_timing_override')}
          </label>
        </div>
        {overrideEnabled && (
          <div className="mt-3 space-y-2" data-testid="payment-timing-override-options">
            <label className="flex items-center gap-2 text-sm text-ds-text">
              <input
                type="radio"
                name="service-payment-timing-value"
                checked={form.payment_timing === 'upon_booking'}
                onChange={() => onChange({ ...form, payment_timing: 'upon_booking' })}
                className={RADIO_INPUT}
              />
              {t('booking_policies.upon_booking')}
            </label>
            <label className="flex items-center gap-2 text-sm text-ds-text">
              <input
                type="radio"
                name="service-payment-timing-value"
                checked={form.payment_timing === 'at_appointment'}
                onChange={() => onChange({ ...form, payment_timing: 'at_appointment' })}
                className={RADIO_INPUT}
              />
              {t('booking_policies.at_appointment')}
            </label>
            <p className={cn('mt-1', FORM_HINT_TYPE)}>
              {t('booking_policies.service_payment_timing_help')}
            </p>
          </div>
        )}
      </fieldset>

      <Input
        name="service-deposit"
        label={t('booking_policies.deposit_amount')}
        type="number"
        min={0}
        step={1}
        value={((form.deposit_amount_cents ?? 0) / 100).toString()}
        onChange={(e) =>
          onChange({
            ...form,
            deposit_amount_cents: Math.round(Number(e.target.value) * 100),
          })
        }
      />
    </>
  );
}

export function BookingRequirementsFields({
  requirements,
  onRequirementsChange,
  practitionerEligibility,
  onPractitionerEligibilityChange,
  selectedPractitionerIds,
  onSelectedPractitionerIdsChange,
  members,
  resourceGroups,
  resources,
}: {
  requirements: ServiceResourceRequirementInput[];
  onRequirementsChange: (next: ServiceResourceRequirementInput[]) => void;
  practitionerEligibility: PractitionerEligibilityMode;
  onPractitionerEligibilityChange: (next: PractitionerEligibilityMode) => void;
  selectedPractitionerIds: string[];
  onSelectedPractitionerIdsChange: (next: string[]) => void;
  members: Array<{ user_id: string; name: string }>;
  resourceGroups: Array<{ id: string; name: string; is_active: boolean }>;
  resources: Array<{
    id: string;
    name: string;
    resource_group_id: string;
    is_active: boolean;
  }>;
}) {
  const { t } = useTranslation();

  function addRequirement() {
    onRequirementsChange([
      ...requirements,
      {
        resource_group_id: '',
        quantity: 1,
        eligibility_mode: 'any',
        eligible_resource_ids: [],
      },
    ]);
  }

  return (
    <div className="space-y-6">
      <fieldset>
        <legend className={FORM_LABEL_TYPE}>{t('services.practitioners')}</legend>
        <div className="mt-2 space-y-2">
          <label className="flex items-center gap-2 text-sm text-ds-text">
            <input
              type="radio"
              name="practitioner-eligibility"
              checked={practitionerEligibility === 'all'}
              onChange={() => onPractitionerEligibilityChange('all')}
              className={RADIO_INPUT}
            />
            {t('services.practitioners_all')}
          </label>
          <label className="flex items-center gap-2 text-sm text-ds-text">
            <input
              type="radio"
              name="practitioner-eligibility"
              checked={practitionerEligibility === 'selected'}
              onChange={() => onPractitionerEligibilityChange('selected')}
              className={RADIO_INPUT}
            />
            {t('services.practitioners_selected')}
          </label>
        </div>
        {practitionerEligibility === 'selected' && (
          <div
            data-testid="practitioner-checklist"
            role="group"
            aria-label={t('services.practitioners_selected')}
            className="mt-3 max-h-48 space-y-1 overflow-y-auto rounded-md border border-ds-line p-2"
          >
            {members.map((member) => {
              const checked = selectedPractitionerIds.includes(member.user_id);
              return (
                <label
                  key={member.user_id}
                  className="flex items-center gap-2 text-sm text-ds-text"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      onSelectedPractitionerIdsChange(
                        checked
                          ? selectedPractitionerIds.filter((id) => id !== member.user_id)
                          : [...selectedPractitionerIds, member.user_id],
                      );
                    }}
                    className={RADIO_INPUT}
                  />
                  {member.name}
                </label>
              );
            })}
            {members.length === 0 && (
              <p className={FORM_HINT_TYPE}>{t('services.practitioners_empty')}</p>
            )}
          </div>
        )}
      </fieldset>

      <div>
        <p className={FORM_LABEL_TYPE}>{t('services.resources_required')}</p>
        <p className={cn('mb-3', FORM_HINT_TYPE)}>{t('services.resources_required_hint')}</p>
        {requirements.length === 0 ? (
          <div
            data-testid="resources-empty"
            className="rounded-md border border-dashed border-ds-line px-3 py-4 text-sm text-ds-muted"
          >
            <p>{t('services.resources_none')}</p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={addRequirement}
            >
              <Plus className="mr-1 h-4 w-4" />
              {t('services.add_resource_requirement')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {requirements.map((req, index) => {
              const groupResources = resources.filter(
                (resource) => resource.resource_group_id === req.resource_group_id,
              );
              return (
                <div
                  key={`${req.resource_group_id}-${index}`}
                  className="space-y-2 rounded-md border border-ds-line p-3"
                >
                  <div className="grid grid-cols-[1fr_5rem_auto] gap-2">
                    <Select
                      value={req.resource_group_id}
                      onChange={(e) => {
                        const next = [...requirements];
                        next[index] = {
                          ...req,
                          resource_group_id: e.target.value,
                          eligible_resource_ids: [],
                          eligibility_mode: 'any',
                        };
                        onRequirementsChange(next);
                      }}
                    >
                      <option value="">{t('services.resource_group_placeholder')}</option>
                      {resourceGroups
                        .filter((group) => group.is_active || group.id === req.resource_group_id)
                        .map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                    </Select>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={req.quantity}
                      onChange={(e) => {
                        const next = [...requirements];
                        next[index] = {
                          ...req,
                          quantity: Math.max(1, Math.min(100, Number(e.target.value) || 1)),
                        };
                        onRequirementsChange(next);
                      }}
                      className={cn(FIELD_CONTROL, 'px-2')}
                      aria-label={t('services.resource_quantity')}
                    />
                    <Button
                      type="button"
                      variant="tertiary"
                      size="icon"
                      onClick={() =>
                        onRequirementsChange(requirements.filter((_, i) => i !== index))
                      }
                      aria-label={t('common.delete')}
                    >
                      <X />
                    </Button>
                  </div>
                  {req.resource_group_id && groupResources.length > 0 && (
                    <div>
                      <p className={cn('mb-1 font-medium text-ds-muted', TYPE.small)}>
                        {t('services.eligible_resources')}
                      </p>
                      <div className="space-y-1">
                        {groupResources.map((resource) => {
                          const selected =
                            req.eligibility_mode !== 'selected' ||
                            req.eligible_resource_ids.includes(resource.id);
                          return (
                            <label
                              key={resource.id}
                              className="flex items-center gap-2 text-sm text-ds-text"
                            >
                              <input
                                type="checkbox"
                                checked={req.eligibility_mode !== 'selected' ? true : selected}
                                onChange={() => {
                                  const currentlySelected =
                                    req.eligibility_mode === 'selected'
                                      ? req.eligible_resource_ids
                                      : groupResources.map((item) => item.id);
                                  const nextIds = currentlySelected.includes(resource.id)
                                    ? currentlySelected.filter((id) => id !== resource.id)
                                    : [...currentlySelected, resource.id];
                                  const next = [...requirements];
                                  const every = nextIds.length === groupResources.length;
                                  next[index] = {
                                    ...req,
                                    eligibility_mode: every ? 'any' : 'selected',
                                    eligible_resource_ids: every ? [] : nextIds,
                                  };
                                  onRequirementsChange(next);
                                }}
                                className={RADIO_INPUT}
                              />
                              {resource.name}
                              {!resource.is_active ? ` (${t('settings.resources.inactive')})` : ''}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <Button type="button" variant="secondary" size="sm" onClick={addRequirement}>
              <Plus className="mr-1 h-4 w-4" />
              {t('services.add_resource_requirement')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function applyAutomations(
  form: ServiceInput,
  automations: QuestionnaireAutomationDraft[],
): ServiceInput {
  const legacy = legacyFieldsFromAutomations(automations);
  return {
    ...form,
    questionnaire_automations: automations,
    ...legacy,
  };
}

export function QuestionnairesFields({
  form,
  onChange,
  templates,
  templatesLoading,
}: {
  form: ServiceInput;
  onChange: (next: ServiceInput) => void;
  templates: FormsTemplateSummary[];
  templatesLoading: boolean;
}) {
  const { t } = useTranslation();
  const automations =
    form.questionnaire_automations ??
    automationsFromLegacyFields({
      pre_visit_questionnaire_template_id: form.pre_visit_questionnaire_template_id,
      pre_visit_hours_before: form.pre_visit_hours_before,
      pre_visit_delay_after_booking_hours: form.pre_visit_delay_after_booking_hours,
      auto_send_questionnaires: form.auto_send_questionnaires,
    });

  if (templatesLoading) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-ds-muted">
        <Loader2 className="h-3 w-3 animate-spin" />
        {t('common.loading', 'Loading…')}
      </div>
    );
  }

  if (templates.length === 0) {
    return <p className={FORM_HINT_TYPE}>{t('services.field_auto_send_no_templates')}</p>;
  }

  return (
    <div className="space-y-6">
      {automations.length === 0 && (
        <p className={FORM_HINT_TYPE} data-testid="questionnaires-empty">
          {t('services.questionnaires_none')}
        </p>
      )}
      <QuestionnairePhaseList
        phase="before_appointment"
        automations={automations}
        templates={templates}
        onChange={(next) => onChange(applyAutomations(form, next))}
      />
      <QuestionnairePhaseList
        phase="after_appointment"
        automations={automations}
        templates={templates}
        onChange={(next) => onChange(applyAutomations(form, next))}
      />
    </div>
  );
}
